import { useRef, useState, useEffect, useCallback } from "react"
import type { RefObject } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { AIAgentViewerIcon } from "components/ui/icons/iaAgent/AIAgentViewerIcon"
import { Splitter } from "components/ui/splitter/Splitter"
import { FeedbackPanel } from "components/ui/FeedbackPanel/FeedbackPanel"
import { AIAgent } from "components/pages/PageAllFiles/layout/FileInfo/layout/DocumentModal/layout/AIAget/AIAgent"
import { useChatStore } from "store/aiAgent.store"
import { useViewerStore } from "store/viewer.store"
import type { TDrawingStatus } from "types/drawer.type"
import style from "./QuotationViewerModal.module.scss"

const {
	container,
	wrapper,
	pdfViewerContainer,
	host,
	aiAgentButton,
	aiAgentContainer,
	aiAgentContainerOpen,
	feedbackPanelContainer,
	feedbackPanelContainerOpen,
	splitterStyles,
} = style

type TPanelSizes = [string | number, string | number, string | number]

type Props = {
	open: boolean
	onClose: () => void
	// Slot for the single shared Apryse host (owned by QuotationViewerModal); the host is the
	// middle Splitter panel, flanked by the feedback + AI-agent panels. Mirrors DocumentModal. #1119
	slotRef: RefObject<HTMLDivElement>
	drawingId: number
	processingId: number
	processingUrl: string
	drawingStatus: TDrawingStatus
}

export const QuotationPreviewModal = ({
	open,
	onClose,
	slotRef,
	drawingId,
	processingId,
	processingUrl,
	drawingStatus,
}: Props) => {
	const { isChatOpen, openChat } = useChatStore()
	const { isFeedbackPanelOpen, closeFeedbackPanel } = useViewerStore()

	const [panelSizes, setPanelSizes] = useState<TPanelSizes>(["0%", "100%", "0%"])
	const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Drive the feedback / AI-agent side panel sizes (mirrors DocumentModal).
	useEffect(() => {
		if (isFeedbackPanelOpen && isChatOpen) {
			setPanelSizes(["25%", "50%", "25%"])
		} else if (isFeedbackPanelOpen) {
			setPanelSizes(["25%", "75%", "0%"])
		} else if (isChatOpen) {
			setPanelSizes(["0%", "50%", "50%"])
		} else {
			setPanelSizes(["0%", "100%", "0%"])
		}

		if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current)
		resizeTimeoutRef.current = setTimeout(() => {
			try {
				window.dispatchEvent(new Event("resize"))
			} catch (error) {
				console.warn("Could not trigger resize event:", error)
			}
		}, 100)
	}, [isChatOpen, isFeedbackPanelOpen])

	useEffect(() => {
		return () => {
			if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current)
		}
	}, [])

	const handleResize = useCallback(
		(sizes: number[] | string[] | undefined) => {
			if ((isChatOpen || isFeedbackPanelOpen) && sizes) {
				setPanelSizes(sizes as TPanelSizes)
				if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current)
				resizeTimeoutRef.current = setTimeout(() => {
					try {
						window.dispatchEvent(new Event("resize"))
					} catch (error) {
						console.warn("Could not update WebViewer layout:", error)
					}
				}, 150)
			}
		},
		[isChatOpen, isFeedbackPanelOpen],
	)

	const panels = [
		{
			children: (
				<div
					className={`${feedbackPanelContainer} ${isFeedbackPanelOpen ? feedbackPanelContainerOpen : ""}`}
					style={{ display: isFeedbackPanelOpen ? "block" : "none" }}
				>
					{isFeedbackPanelOpen && (
						<FeedbackPanel
							onClose={closeFeedbackPanel}
							drawingId={drawingId}
							processingId={processingId}
							processingUrl={processingUrl}
							drawingStatus={drawingStatus}
						/>
					)}
				</div>
			),
			size: panelSizes[0],
			resizable: isFeedbackPanelOpen,
			min: isFeedbackPanelOpen ? 300 : 0,
			max: isFeedbackPanelOpen ? undefined : 0,
			id: "feedback-panel",
		},
		{
			children: (
				<div className={pdfViewerContainer}>
					<div ref={slotRef} className={host} />
					{!isChatOpen && (
						<ButtonPrimary onClick={openChat} className={aiAgentButton}>
							<AIAgentViewerIcon />
							AI Agent
						</ButtonPrimary>
					)}
				</div>
			),
			size: panelSizes[1],
			resizable: isChatOpen || isFeedbackPanelOpen,
			min: isChatOpen || isFeedbackPanelOpen ? 400 : 0,
			id: "pdf-viewer",
		},
		{
			children: (
				<div
					className={`${aiAgentContainer} ${isChatOpen ? aiAgentContainerOpen : ""}`}
					style={{ display: isChatOpen ? "block" : "none" }}
				>
					{isChatOpen && <AIAgent />}
				</div>
			),
			size: panelSizes[2],
			resizable: isChatOpen,
			min: isChatOpen ? 400 : 0,
			max: isChatOpen ? undefined : 0,
			id: "ai-agent",
		},
	]

	return (
		<Modal isModalOpen={open} setModalClose={onClose} width="100%">
			<div className={container}>
				<div className={wrapper}>
					<Splitter layout="horizontal" panels={panels} onResize={handleResize} className={splitterStyles} />
				</div>
			</div>
		</Modal>
	)
}
